/**
 * Builder class for creating Schema.org JSON-LD objects with optional inLanguage property
 * 
 * @example
 * ```typescript
 * const schema = new SchemaBuilder()
 *   .setContext('https://schema.org')
 *   .setType('Product')
 *   .setProperty('name', 'Toronto Real Estate')
 *   .setProperty('description', 'Houses for sale')
 *   .setInLanguage('en-CA')
 *   .build();
 * ```
 */
export class SchemaBuilder<T extends Record<string, unknown> = Record<string, unknown>> {
  private schema: T;

  constructor(initialSchema?: Partial<T>) {
    this.schema = (initialSchema || {}) as T;
  }

  /**
   * Sets the @context property for the schema
   */
  setContext(context: string): this {
    this.schema = { ...this.schema, '@context': context } as T;
    return this;
  }

  /**
   * Sets the @type property for the schema
   */
  setType(type: string): this {
    this.schema = { ...this.schema, '@type': type } as T;
    return this;
  }

  /**
   * Sets a property on the schema
   */
  setProperty<K extends keyof T>(key: K, value: T[K]): this {
    this.schema = { ...this.schema, [key]: value };
    return this;
  }

  /**
   * Sets multiple properties on the schema at once
   */
  setProperties(properties: Partial<T>): this {
    this.schema = { ...this.schema, ...properties };
    return this;
  }

  /**
   * Sets the inLanguage property if locale is provided
   * @param locale The locale code (e.g., 'en-CA', 'fr-CA')
   */
  setInLanguage(locale?: string): this {
    if (locale) {
      this.schema = { ...this.schema, inLanguage: locale } as T;
    }
    return this;
  }

  /**
   * Builds and returns the final schema object
   */
  build(): T {
    return this.schema;
  }
}
